import { useTheme } from "next-themes";
import { Toaster as Sonner, ToasterProps } from "sonner";
import React from "react";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleXIcon,
  InfoIcon,
  LoaderIcon,
} from "lucide-react";

/**
 * What each kind of toast looks like.
 *
 * Exported so the one rule that matters can be asserted rather than reviewed:
 * **a success may not wear the colour of a failure.** It did — `success` was
 * `bg-primary`, and `--primary` is the Holtzbrinck brand red, so every
 * confirmation in the app arrived looking like an error and read as one until
 * you got to the wording (NAV-79). Two costs, not one: the app felt broken
 * while it was working, and red stopped carrying the warning it exists to
 * carry.
 *
 * The brand red keeps its place everywhere it means "this is the thing to
 * click" — primary buttons, CTAs. What it may not do is answer a question the
 * user asked with an outcome colour.
 */
export const TOAST_CLASSNAMES = {
  toast:
    "text-white min-h-12 rounded-lg grid grid-cols-[auto_1fr_auto_auto] pl-2 overflow-hidden",
  content: "py-2 pr-4 text-sm flex items-center justify-center",
  icon: "h-full! w-auto! flex items-center justify-center pl-3 pr-3",

  // Buttons
  actionButton:
    "w-12 flex items-center justify-center border-l border-white/20 hover:bg-black/10",
  cancelButton:
    "w-12 flex items-center justify-center border-l border-white/20 hover:bg-black/10",

  // Copy
  title: "leading-6 font-normal",
  description: "leading-6 font-normal",

  // Toast colors. One feedback token each, all three defined in
  // tailwind.config.js with the contrast reasoning; white text comes from
  // `toast` above.
  success: "bg-system-success",
  warning: "bg-system-warning",
  error: "bg-system-error",
  info: "bg-secondary-600",
} as const;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position="bottom-center"
      icons={{
        // Explicit icons so success/info cannot swap: a spinner must never
        // announce a completed action, nor a checkmark a neutral note.
        success: <CircleCheckIcon className="w-5 h-5" />,
        warning: <CircleAlertIcon className="w-5 h-5" />,
        error: <CircleXIcon className="w-5 h-5" />,
        info: <InfoIcon className="w-5 h-5" />,
        loading: <LoaderIcon className="w-5 h-5" />,
      }}
      style={
        {
          "--width": "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as React.CSSProperties
      }
      // No background on the container itself. It carried `bg-red-500`, which
      // painted red into every gap the toasts left — around their rounded
      // corners, and for the width of the strip on a narrow one — so even a
      // toast coloured correctly sat on red.
      toastOptions={{
        unstyled: true,
        classNames: TOAST_CLASSNAMES,
      }}
      {...props}
    />
  );
};

export { Toaster };
